import type { Disposable } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import type { Config, Driver } from 'driver.js';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED } from '@/constants/track-events';
import {
    ReportGenerationStatusService,
    type ReportGenerationStatusService as ReportGenerationStatusServiceType,
} from '@/domains/novelty-check-report/services/report-generation-status-service';
import { CurrentTaskService, type CurrentTaskService as CurrentTaskServiceType } from '@/services/current-task/current-task-service';
import {
    OnboardingEligibilityService,
    type OnboardingEligibilityService as OnboardingEligibilityServiceType,
} from '../../common/services/onboarding-eligibility-service';
import {
    ACTION_GUIDE_OVERLAY_COLOR,
    ACTION_GUIDE_OVERLAY_OPACITY,
} from '../../common/constants';
import { createReportActionGuideStep } from '../../common/utils/report-action-guide';
import { registerOnboardingLocaleMessages } from '../../common/utils/onboarding-locale';
import { messages } from '../../common/locales';
import {
    OnboardingStateService,
    type OnboardingStateService as OnboardingStateServiceType,
} from '../../common/services/onboarding-state-service';
import {
    ReportActionGuideBubblePresenter,
    type ReportActionGuideBubblePresenter as ReportActionGuideBubblePresenterType,
} from '../../common/services/report-action-guide-bubble-presenter';
import {
    NoveltySearchOnboardingRuntime,
    type NoveltySearchOnboardingRuntime as NoveltySearchOnboardingRuntimeType,
} from '../novelty-search-onboarding-runtime';
import type { NoveltySearchReportValueOnboardingService } from '../novelty-search-report-value-onboarding-service';

/** 报告滚动容器的稳定选择器。 */
const REPORT_CONTENT_SELECTOR = '[data-testid="novelty-search-report-content"]';
/** 分享按钮的稳定选择器。 */
const SHARE_SELECTOR = '[data-testid="share-link-button"]';
/** 导出按钮的稳定选择器。 */
const EXPORT_SELECTOR = '[data-testid="novelty-search-report-export"]';
/** Share 作为 Driver 辅助目标时使用的高亮样式类。 */
const SECONDARY_HIGHLIGHT_CLASS = 'novelty-search-report-action-secondary-highlight';
/** 达到阅读阈值后，展示操作引导前要求的停留时长。 */
const ACTION_SPOTLIGHT_DELAY_MS = 800;
/** 等待报告内容和操作按钮渲染完成的最长时间。 */
const TARGET_WAIT_TIMEOUT_MS = 5000;
/** 用户阅读报告达到 20% 后，开始准备 Share/Export 操作引导。 */
const READING_PROGRESS_THRESHOLD = 0.2;

/** 当前报告操作引导所需的身份和任务上下文。 */
interface OnboardingContext {
    /** 当前报告任务 ID。 */
    taskId: string;
    /** 当前浏览器用户的稳定身份 ID。 */
    identityId: string;
}

/**
 * 查新报告完成后的分享与导出操作引导编排器。
 *
 * 该类只负责业务时序：监听报告刷新完成、检测阅读进度，
 * 并协调 Driver 遮罩与双目标静态高亮。具体 DOM 和视觉实现由 runtime 负责。
 */
@injectable()
export class NoveltySearchReportValueOnboardingServiceImpl implements NoveltySearchReportValueOnboardingService {
    /** 当前正在处理的报告和用户身份上下文。 */
    private _context?: OnboardingContext;
    /** 用于监听滚动、点击和计算阅读进度的报告容器。 */
    private _reportContentElement?: Element;
    /** 当前报告页面上的 Share 按钮。 */
    private _shareElement?: Element;
    /** 当前报告页面上的 Export 按钮。 */
    private _exportElement?: Element;
    /** 当前操作高亮使用的 Driver.js 实例。 */
    private _driver?: Driver;
    /** 分享按钮说明气泡的清理句柄。 */
    private _shareGuideBubbleDisposable?: Disposable;
    /** 报告生成完成事件的订阅清理句柄。 */
    private _reportGeneratedDisposable?: Disposable;
    /** 达到阅读阈值后的停留检测定时器。 */
    private _readingPauseTimerId?: number;
    /** 当前报告的 Share/Export 操作引导是否已经展示。 */
    private _isActionSpotlightShown = false;
    /** 当前 Share/Export 点击是否仍可消费一次引导归因。 */
    private _isActionSpotlightAttributionArmed = false;
    /** 报告容器的滚动和点击监听是否已经绑定。 */
    private _isListenersBound = false;
    /** Service 是否已经初始化并处于可响应事件状态。 */
    private _isInitialized = false;
    /** 当前生命周期的报告资格是否已经完成判断。 */
    private _isEligibilityResolved = false;
    /** 当前生命周期的报告资格判断结果。 */
    private _isReportEligible = false;
    /** 当前生命周期是否正在请求报告资格。 */
    private _isEligibilityRequestPending = false;
    /** 资格判断期间是否收到过报告生成完成事件。 */
    private _hasPendingReportGenerated = false;
    /** 本地调试是否已显式绕过公共资格门禁。 */
    private _isDebugMode = false;
    /** 当前正在异步启动 onboarding 的任务 ID。 */
    private _startingTaskId?: string;
    /** 当前生命周期内已经完成启动处理的任务 ID。 */
    private _startedTaskId?: string;
    /** 用于使销毁前启动的异步任务失效，避免路由切换后继续挂载引导。 */
    private _lifecycleId = 0;

    // eslint-disable-next-line max-params
    constructor(
        /** 封装 Driver、DOM、身份、存储和定时器等浏览器能力。 */
        @inject(NoveltySearchOnboardingRuntime)
        private readonly _runtime: NoveltySearchOnboardingRuntimeType,
        /** 用于上报报告操作引导埋点。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
        /** 提供报告数据刷新完成状态和完成事件。 */
        @inject(ReportGenerationStatusService)
        private readonly _reportGenerationStatusService: ReportGenerationStatusServiceType,
        /** 提供当前页面对应的报告任务 ID。 */
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskServiceType,
        /** 独立判断并固化账号级报告 onboarding 资格。 */
        @inject(OnboardingEligibilityService)
        private readonly _onboardingEligibilityService: OnboardingEligibilityServiceType,
        /** 提供账号级报告引导后端状态。 */
        @inject(OnboardingStateService)
        private readonly _onboardingStateService: OnboardingStateServiceType,
        /** 提供报告操作说明的当前语言文案。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 为 Driver 辅助目标展示与导出按钮一致的说明气泡。 */
        @inject(ReportActionGuideBubblePresenter)
        private readonly _reportActionGuideBubblePresenter: ReportActionGuideBubblePresenterType,
    ) {}

    /**
     * 初始化报告操作引导。
     *
     * 重复调用不会重复订阅；报告资格只在订阅收到真实完成事件后解析。
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) return;
        this._isInitialized = true;
        // setup 阶段先订阅，确保不会错过子组件完成报告刷新的事件。
        this._reportGeneratedDisposable = this._reportGenerationStatusService.onReportGenerated(
            this._handleReportGenerated,
        );
    }

    /** 消费一次报告操作引导归因，并同步清理遮罩与静态高亮。 */
    consumeActionGuide(): boolean {
        if (!this._isActionSpotlightAttributionArmed) return false;

        this._isActionSpotlightAttributionArmed = false;
        this._clearActionSpotlight();
        return true;
    }

    /** 仅在 Vite 开发环境绕过受众判断，强制启动一次报告操作引导监听。 */
    debugStart(taskId: string): void {
        if (!import.meta.env.DEV || !taskId) return;
        if (!this._isInitialized) this._isInitialized = true;
        this._isDebugMode = true;
        this._isEligibilityResolved = true;
        this._isReportEligible = true;
        this._tryStartReport(taskId);
    }

    /** 销毁当前生命周期内的订阅、异步流程、弹窗、Driver 和页面监听。 */
    destroy(): void {
        this._isInitialized = false;
        this._isEligibilityResolved = false;
        this._isReportEligible = false;
        this._isEligibilityRequestPending = false;
        this._hasPendingReportGenerated = false;
        this._isDebugMode = false;
        this._lifecycleId += 1;
        this._reportGeneratedDisposable?.dispose();
        this._reportGeneratedDisposable = undefined;
        this._startingTaskId = undefined;
        this._startedTaskId = undefined;
        this._clearRuntimeState();
        this._clearReportContext();
    }

    /** 报告数据刷新完成后的事件处理器。 */
    private readonly _handleReportGenerated = () => {
        if (!this._isInitialized) return;
        this._hasPendingReportGenerated = true;
        this._resolveReportEligibility();
    };

    /** 在真实报告完成后解析一次独立报告资格，并继续处理期间缓存的事件。 */
    // eslint-disable-next-line complexity
    private _resolveReportEligibility(): void {
        if (!this._isInitialized || this._isDebugMode) return;
        if (this._isEligibilityResolved) {
            const shouldStart = this._isReportEligible && this._hasPendingReportGenerated;
            this._hasPendingReportGenerated = false;
            if (shouldStart) this._tryStartCurrentReport();
            return;
        }
        if (this._isEligibilityRequestPending) return;

        const lifecycleId = this._lifecycleId;
        this._isEligibilityRequestPending = true;
        void this._onboardingEligibilityService.isReportEligible().then((isEligible) => {
            if (!this._isCurrent(lifecycleId) || this._isDebugMode) return;

            this._isEligibilityResolved = true;
            this._isReportEligible = isEligible;
            const shouldStart = isEligible && this._hasPendingReportGenerated;
            this._hasPendingReportGenerated = false;
            if (!isEligible) {
                this._reportGeneratedDisposable?.dispose();
                this._reportGeneratedDisposable = undefined;
                return;
            }
            if (shouldStart) this._tryStartCurrentReport();
        }).catch((error) => {
            if (!this._isCurrent(lifecycleId) || this._isDebugMode) return;
            this._isEligibilityResolved = true;
            this._isReportEligible = false;
            this._hasPendingReportGenerated = false;
            this._reportGeneratedDisposable?.dispose();
            this._reportGeneratedDisposable = undefined;
            this._runtime.warn('Failed to resolve novelty search report onboarding eligibility:', error);
        }).finally(() => {
            if (this._isCurrent(lifecycleId)) this._isEligibilityRequestPending = false;
        });
    }

    /** 按当前任务尝试启动一次报告操作引导流程。 */
    private _tryStartCurrentReport(): void {
        this._tryStartReport(this._currentTaskService.taskId);
    }

    /**
     * 按指定任务尝试启动一次报告操作引导流程。
     * @param taskId 当前报告任务 ID；本地调试时可使用独立的临时 ID。
     */
    private _tryStartReport(taskId: string | undefined): void {
        if (!this._isInitialized) return;

        // 同一报告无论收到多少次完成通知，都只允许一个启动流程。
        if (!taskId || this._startingTaskId === taskId || this._startedTaskId === taskId) return;

        const lifecycleId = this._lifecycleId;
        this._startingTaskId = taskId;
        void this._start(taskId, lifecycleId).then((started) => {
            if (!this._isCurrent(lifecycleId)) return;
            if (started) this._startedTaskId = taskId;
        }).catch((error) => {
            if (this._isCurrent(lifecycleId)) {
                this._clearRuntimeState();
                this._clearReportContext();
            }
            this._runtime.warn('Failed to start novelty search report value onboarding:', error);
        }).finally(() => {
            if (this._startingTaskId === taskId) this._startingTaskId = undefined;
        });
    }

    /**
     * 完成当前任务的身份、目标和历史状态解析，并启动尚未展示的阶段。
     * @param taskId 当前报告任务 ID。
     * @param lifecycleId 启动时捕获的生命周期编号。
     * @returns 是否已经成功处理当前任务的 onboarding 状态。
     */
    // eslint-disable-next-line complexity
    private async _start(taskId: string, lifecycleId: number): Promise<boolean> {
        // 先解析身份和全部必要目标，任一目标缺失时不展示残缺的引导。
        const context = await this._resolveContext(taskId, lifecycleId);
        if (!context) return false;

        const onboardingState = this._isDebugMode
            ? {}
            : await this._onboardingStateService.getState(context.identityId);
        if (!this._isCurrent(lifecycleId)) return false;
        if (onboardingState.novelty_report_actions_seen) {
            this._isActionSpotlightShown = true;
            return true;
        }

        const targets = await this._resolveTargets(lifecycleId);
        if (!targets || !this._isCurrent(lifecycleId)) return false;

        this._clearRuntimeState();
        this._context = context;
        this._reportContentElement = targets.reportContentElement;
        this._shareElement = targets.shareElement;
        this._exportElement = targets.exportElement;
        this._isActionSpotlightShown = false;

        this._bindListeners();
        return true;
    }

    /**
     * 获取当前用户身份并构建报告操作引导上下文。
     * @param taskId 当前报告任务 ID。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _resolveContext(taskId: string, lifecycleId: number): Promise<OnboardingContext | undefined> {
        const identityId = await this._runtime.getIdentityId();
        if (!this._isCurrent(lifecycleId) || !identityId) return;

        return {
            taskId,
            identityId,
        };
    }

    /**
     * 等待报告容器、Share 和 Export 三个必要目标完成渲染。
     * @param lifecycleId 启动时捕获的生命周期编号。
     */
    private async _resolveTargets(lifecycleId: number): Promise<{
        reportContentElement: Element;
        shareElement: Element;
        exportElement: Element;
    } | undefined> {
        await this._runtime.waitForRender();
        if (!this._isCurrent(lifecycleId)) return;

        const [reportContentElement, shareElement, exportElement] = await Promise.all([
            this._runtime.waitForElement(REPORT_CONTENT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
            this._runtime.waitForElement(SHARE_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
            this._runtime.waitForElement(EXPORT_SELECTOR, TARGET_WAIT_TIMEOUT_MS),
        ]);
        if (!this._isCurrent(lifecycleId) || !reportContentElement || !shareElement || !exportElement) return;

        return { reportContentElement, shareElement, exportElement };
    }

    /** 绑定报告内容区域的滚动和点击监听。 */
    private _bindListeners(): void {
        if (this._isListenersBound || !this._reportContentElement) return;
        this._runtime.addEventListener(this._reportContentElement, 'scroll', this._handleContentScroll);
        this._runtime.addEventListener(this._reportContentElement, 'click', this._handleContentClick);
        this._isListenersBound = true;
    }

    /** 移除报告内容区域的滚动和点击监听。 */
    private _unbindListeners(): void {
        if (!this._isListenersBound || !this._reportContentElement) return;
        this._runtime.removeEventListener(this._reportContentElement, 'scroll', this._handleContentScroll);
        this._runtime.removeEventListener(this._reportContentElement, 'click', this._handleContentClick);
        this._isListenersBound = false;
    }

    /**
     * 报告滚动处理器。
     *
     * 滚动用于检测阅读进度，操作引导已展示时则负责清理引导。
     */
    private readonly _handleContentScroll: EventListener = () => {
        if (this._driver?.isActive()) {
            this._driver.refresh();
            return;
        }
        this._scheduleActionSpotlight();
    };

    /** 报告内容点击处理器，用于关闭已经激活的操作引导。 */
    private readonly _handleContentClick: EventListener = () => {
        if (this._driver?.isActive()) this._clearActionSpotlight();
    };

    /** 页面非目标指针或键盘交互处理器，用于结束 Share/Export 操作引导。 */
    private readonly _handleDocumentInteraction: EventListener = (event) => {
        if (this._shouldPreserveActionTargetInteraction(event)) return;
        this._clearActionSpotlight();
    };

    /** 目标按钮的指针或激活键交互需留给业务点击同步消费归因。 */
    private _shouldPreserveActionTargetInteraction(event: Event): boolean {
        if (!this._isActionTarget(event.target)) return false;
        if (event.type === 'pointerdown') return true;
        return event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ');
    }

    /** 判断事件来源是否为当前分享或导出按钮。 */
    private _isActionTarget(target: EventTarget | null): boolean {
        if (!(target instanceof Node)) return false;
        return Boolean(
            this._shareElement?.contains(target)
            || this._exportElement?.contains(target),
        );
    }

    /** 在阅读进度达到 20% 且停留 800ms 后调度 Share/Export 操作引导。 */
    private _scheduleActionSpotlight(): void {
        if (this._isActionSpotlightShown || !this._reportContentElement) return;
        const progress = this._runtime.getReadingProgress(this._reportContentElement);
        if (progress < READING_PROGRESS_THRESHOLD) {
            this._clearReadingPauseTimer();
            return;
        }

        // 达到阈值后等待短暂停留，滚动期间会持续重置，避免阅读过程中突然打断用户。
        this._clearReadingPauseTimer();
        const lifecycleId = this._lifecycleId;
        this._readingPauseTimerId = this._runtime.setTimeout(() => {
            this._readingPauseTimerId = undefined;
            if (!this._isCurrent(lifecycleId) || !this._reportContentElement) return;
            if (this._runtime.getReadingProgress(this._reportContentElement) < READING_PROGRESS_THRESHOLD) return;
            this._showActionSpotlight();
        }, ACTION_SPOTLIGHT_DELAY_MS);
    }

    /** 创建 Driver 遮罩，以 Export 为主目标并静态提升 Share。 */
    // eslint-disable-next-line complexity
    private _showActionSpotlight(): void {
        const context = this._context;
        const shareElement = this._shareElement;
        const exportElement = this._exportElement;
        if (!context || !shareElement || !exportElement || this._isActionSpotlightShown) return;
        if (!this._runtime.isElementEnabled(shareElement) || !this._runtime.isElementEnabled(exportElement)) return;

        try {
            const driverInstance = this._runtime.createDriver(this._createDriverConfig());
            this._driver = driverInstance;
            this._runtime.addClass(shareElement, SECONDARY_HIGHLIGHT_CLASS);
            // Export 是 Driver 主目标，Share 通过辅助高亮保持在同一遮罩层上方。
            registerOnboardingLocaleMessages(this._i18nService, messages);
            driverInstance.highlight(createReportActionGuideStep(
                exportElement,
                this._i18nService.t('onboarding.reportActions.exportDescription'),
            ));
            if (!driverInstance.isActive()) {
                this._clearActionSpotlight(false);
                return;
            }
            this._shareGuideBubbleDisposable = this._reportActionGuideBubblePresenter.show({
                target: shareElement,
                description: this._i18nService.t('onboarding.reportActions.shareDescription'),
            });

            this._isActionSpotlightAttributionArmed = true;
            this._isActionSpotlightShown = true;
            void this._onboardingStateService.updateState(
                context.identityId,
                { novelty_report_actions_seen: true },
                'optimistic',
            ).catch(error => this._runtime.warn(
                'Failed to persist novelty search report onboarding state:',
                error,
            ));
            this._runtime.addDocumentEventListener('pointerdown', this._handleDocumentInteraction);
            this._runtime.addDocumentEventListener('keydown', this._handleDocumentInteraction);
            this._track(TRACK_EVENT_AGENT_REPORT_ACTIONS_SPOTLIGHTED);
        } catch (error) {
            this._clearActionSpotlight();
            this._runtime.warn('Failed to show novelty search report action spotlight:', error);
        }
    }

    /**
     * 创建报告操作引导的 Driver.js 配置。
     * @returns 允许目标交互且不包含 popover 的高亮配置。
     */
    private _createDriverConfig(): Config {
        return {
            animate: true,
            overlayColor: ACTION_GUIDE_OVERLAY_COLOR,
            overlayOpacity: ACTION_GUIDE_OVERLAY_OPACITY,
            allowClose: true,
            allowScroll: true,
            overlayClickBehavior: () => this._clearActionSpotlight(),
            disableActiveInteraction: false,
            allowKeyboardControl: true,
            stagePadding: 4,
            stageRadius: 8,
            onDestroyed: (_element, _step, { driver: destroyedDriver }) => {
                if (this._driver !== destroyedDriver) return;
                this._clearActionSpotlight(false);
            },
        };
    }

    /**
     * 清理操作引导相关的定时器、监听、辅助样式和 Driver。
     * @param destroyDriver 是否主动销毁 Driver；Driver 自身回调清理时传入 false，避免重复销毁。
     */
    private _clearActionSpotlight(destroyDriver = true): void {
        this._isActionSpotlightAttributionArmed = false;
        this._clearReadingPauseTimer();
        this._runtime.removeDocumentEventListener('pointerdown', this._handleDocumentInteraction);
        this._runtime.removeDocumentEventListener('keydown', this._handleDocumentInteraction);
        this._shareGuideBubbleDisposable?.dispose();
        this._shareGuideBubbleDisposable = undefined;
        if (this._shareElement) this._runtime.removeClass(this._shareElement, SECONDARY_HIGHLIGHT_CLASS);

        const driverInstance = this._driver;
        this._driver = undefined;
        if (destroyDriver && driverInstance?.isActive()) driverInstance.destroy();
    }

    /** 清理当前报告所有可见 onboarding UI、计时器和 DOM 监听。 */
    private _clearRuntimeState(): void {
        // 所有可见 UI 和监听均在这里统一回收，保证 destroy 与异常路径行为一致。
        this._clearActionSpotlight();
        this._unbindListeners();
    }

    /** 清空当前报告目标、上下文和阶段状态，供销毁或启动失败后复用。 */
    private _clearReportContext(): void {
        this._context = undefined;
        this._reportContentElement = undefined;
        this._shareElement = undefined;
        this._exportElement = undefined;
        this._isActionSpotlightShown = false;
    }

    /** 取消达到阅读阈值后的停留检测定时器。 */
    private _clearReadingPauseTimer(): void {
        if (this._readingPauseTimerId === undefined) return;
        this._runtime.clearTimeout(this._readingPauseTimerId);
        this._readingPauseTimerId = undefined;
    }

    /**
     * 判断异步流程是否仍属于当前有效生命周期。
     * @param lifecycleId 异步流程启动时捕获的生命周期编号。
     */
    private _isCurrent(lifecycleId: number): boolean {
        return this._isInitialized && this._lifecycleId === lifecycleId;
    }

    /**
     * 上报 onboarding 埋点；Agent、任务和用户信息由埋点底层统一补充。
     * @param eventCode 埋点事件编码。
     * @param detail 当前事件中会变化且具有分析价值的埋点详情。
     */
    private _track(eventCode: string, detail?: Record<string, string>): void {
        if (!this._context) return;
        void this._ravenService.track(eventCode, {
            eventType: TRACK_EVENT_TYPE.NoveltySearch,
            sourceType: eventCode,
            ...(detail ? { eventDetailStr: detail } : {}),
        }).catch(() => undefined);
    }
}
